import { useCallback, useEffect, useMemo } from "react";
import { useDropzone } from "react-dropzone";
import { filesize } from "filesize";
import { MODELS, useMultiModel } from "../../../api";
import { icons } from "../../../design-system/primitives/icons";
import IconButton from "../../../design-system/primitives/components/IconButton/IconButton";
import Text from "../../../design-system/primitives/components/Text/Text";
import { fileToWebP } from "../../../lib/images.jsx";
import PeertubeVideoEmbed from "../showroom/PeertubeVideoEmbed";
import styles from "./showroomForm.css";

/**
 * The biggest file the form takes. Lives here rather than with the rules in
 * `fields`, so the drop zone can name the limit it is validated against - the
 * two drifting apart is how a form ends up promising the wrong number.
 */
export const MAX_FILE_SIZE_MB = 25;
export const MAX_FILE_SIZE = MAX_FILE_SIZE_MB * 1024 * 1024;

// ---- Values ----

/** The info the backend keeps about a stored file, or a PeerTube video. */
type StoredFileInfo = {
  path?: string;
  mimetype?: string;
  size?: number;
  name?: string;
  /** The PeerTube video. */
  id?: string;
  uuid?: string;
};

/**
 * A file field's value once it has been saved: `{ type, data }` around the
 * info, or — for cover images saved by an older version of the form — the bare
 * info itself.
 */
type StoredFile = StoredFileInfo & {
  type?: "file" | "video";
  data?: StoredFileInfo;
};

export type StoredLink = { type: "link"; data: string };

/** A file field's value: a freshly picked `File`, a stored one, or nothing. */
export type FileValue = File | StoredFile | null;

/** The project upload's value: a file, or a link. */
export type UploadValue = FileValue | StoredLink;

/**
 * A URL for a file the browser is still holding, revoked once it is no longer
 * shown. Anything already on the server is read by its path instead, so this
 * is `null` for it.
 */
export const useObjectUrl = (file: File | null) => {
  const url = useMemo(() => (file ? URL.createObjectURL(file) : null), [file]);
  useEffect(() => {
    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, [url]);

  return url;
};

// ---- Preview ----

const getFileName = (file: File | StoredFile) => {
  if (file.name) return file.name;
  if (file instanceof File) return undefined;
  const filepath = file.path ?? file.data?.path;
  if (filepath) return filepath.substring(filepath.lastIndexOf("/") + 1);
  return undefined;
};

/**
 * Name, human-readable size, and type of a file, whichever shape it comes in.
 *
 * The type is the mimetype, directly on the file or in its data when it comes
 * from the server. A freshly picked file carries it in `file.type`, which is
 * checked last because a stored file's `type` is "file" or "video" instead.
 * A PeerTube video is stored with `type: "video"` too but needs a different
 * preview, so its `uuid` is checked first.
 */
const getFileInfo = (file: File | StoredFile) => {
  const size =
    file instanceof File ? file.size : (file.size ?? file.data?.size);
  const fileType =
    (file instanceof File
      ? file.type
      : file.mimetype ||
        file.data?.mimetype ||
        (file.data?.uuid && "peertube") ||
        file.type) || null;

  return {
    fileName: getFileName(file),
    fileSize: size === undefined ? undefined : filesize(size),
    fileType,
  };
};

const DataPreview = ({
  fileType,
  fileUrl,
  videoId,
}: {
  fileType: string;
  fileUrl: string | null;
  videoId: string | undefined;
}) => {
  if (fileUrl && fileType.startsWith("image")) {
    return (
      <img
        src={fileUrl}
        aria-label="Vorschaubild für eine Datei, die von einem User hochgeladen wurde"
      />
    );
  }
  if (fileUrl && fileType.startsWith("video")) {
    return (
      <video
        controls
        src={fileUrl}
        aria-label="Video-Datei, die von einem User hochgeladen wurde"
      >
        Videovorschau wird von diesem Browser nicht unterstützt.
      </video>
    );
  }
  if (fileUrl && fileType.startsWith("audio")) {
    return (
      <audio
        controls
        src={fileUrl}
        aria-label="Audio-Datei, die von einem User hochgeladen wurde"
      >
        Audiovorschau wird von diesem Browser nicht unterstützt.
      </audio>
    );
  }
  if (fileType.startsWith("peertube") && videoId) {
    return <PeertubeVideoEmbed data={{ id: videoId }} />;
  }
  return null;
};

/**
 * The picked or stored file: its preview, then the file itself as a pill with
 * its name, its size and the button that drops it.
 */
const PreviewFile = ({
  file,
  onRemove,
}: {
  file: File | StoredFile;
  onRemove: () => void;
}) => {
  const storedPath = file instanceof File ? undefined : file.data?.path;
  const [modelData] = useMultiModel([
    storedPath ? MODELS.S3_FILE_URL(storedPath) : null,
  ]);
  const { fileName, fileSize, fileType } = getFileInfo(file);

  // A just picked file is shown from an object URL, a stored one from the
  // server's. A stored PeerTube video embeds its own player and needs neither.
  const objectUrl = useObjectUrl(file instanceof File ? file : null);
  const fileUrl = objectUrl ?? modelData?.fileUrl ?? null;

  return (
    <div className={styles.preview}>
      <div className={styles.media}>
        {fileType && (
          <DataPreview
            fileType={fileType}
            fileUrl={fileUrl}
            videoId={file instanceof File ? undefined : file.data?.id}
          />
        )}
      </div>
      <Text variant="label">Upload</Text>
      <div className={styles.filePill}>
        <div className={styles.fileInfo}>
          {fileName && <Text variant="label">{fileName}</Text>}
          {fileSize && (
            <Text variant="label-small" color="neutral-100">
              {fileSize}
            </Text>
          )}
        </div>
        <IconButton
          icon="trash"
          ariaLabel="Datei entfernen"
          size="small"
          color="neutral"
          variant="tertiary"
          onClick={onRemove}
        />
      </div>
    </div>
  );
};

// ---- Selection ----

/**
 * The drop zone: drag & drop or the file dialog. Picked jpeg/png/gif images
 * are converted to WebP; the original is handed over first so the preview
 * appears while the conversion runs.
 */
const UploadIcon = icons.upload;

const SelectFile = ({
  name,
  label,
  accept,
  onSelect,
  errorId,
}: {
  name: string;
  label: string;
  accept: string;
  onSelect: (file: File) => void;
  errorId: string | undefined;
}) => {
  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      const file = acceptedFiles[0];
      if (!file) return;
      onSelect(file);

      if (
        file.type === "image/jpeg" ||
        file.type === "image/png" ||
        file.type === "image/gif"
      ) {
        void fileToWebP(file).then(onSelect);
      }
    },
    [onSelect],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: false,
  });

  return (
    <div
      {...getRootProps({
        className: styles.droppable({ over: isDragActive }),
      })}
    >
      <input
        {...getInputProps({
          name,
          accept,
          "aria-label": label,
          "aria-describedby": errorId,
        })}
      />
      <div className={styles.iconContainer}>
        <UploadIcon size={20} />
      </div>
      <div className={styles.textGroup}>
        <Text variant="label">Datei auswählen oder hierher bewegen</Text>
        <Text variant="label-small" color="neutral-100">
          {`Maximale Dateigröße bis zu ${MAX_FILE_SIZE_MB} MB`}
        </Text>
      </div>
    </div>
  );
};

// ---- The field ----

/**
 * What the box previews, if anything: a picked file, or a stored file or
 * video. A link belongs to the other tab, and an error hides the preview so
 * the drop zone is there to pick a better file.
 */
const getPreviewFile = (
  value: UploadValue,
  hasError: boolean,
): File | StoredFile | null => {
  if (hasError || !value) return null;
  if (value instanceof File) return value;
  if (value.type === "link") return null;
  // With `data` there has to be content in it; without, it's the legacy cover
  // image shape, which is the file info itself.
  if (value.data)
    return value.type === "file" || value.type === "video" ? value : null;
  return value;
};

interface FileUploadProps {
  name: string;
  /** Names the file input for screen readers. */
  label: string;
  /** The file input's `accept` attribute. */
  accept: string;
  value: UploadValue;
  onChange: (value: FileValue) => void;
  /**
   * The field's error, if it has one. All this needs of it: the preview gives
   * way to the drop zone so a better file can be picked, and the input points
   * at the message the field shows.
   */
  error?: { id: string } | null;
}

/**
 * What a file field holds: the drop zone until a file is picked, its preview
 * after. The panel around it belongs to the field - the project upload puts
 * its tabs in one too - so this renders no panel of its own.
 */
const FileUpload = ({
  name,
  label,
  accept,
  value,
  onChange,
  error,
}: FileUploadProps) => {
  const previewFile = getPreviewFile(value, Boolean(error));

  return previewFile ? (
    <PreviewFile file={previewFile} onRemove={() => onChange(null)} />
  ) : (
    <SelectFile
      name={name}
      label={label}
      accept={accept}
      onSelect={onChange}
      errorId={error?.id}
    />
  );
};

export default FileUpload;
